/* eslint import/no-cycle: off */
import { DebugService } from '../shared/services/debug.service.js';
import { ApiService } from '../core/services/api/api.service.js';
import { REFI_OFFERS_CONST } from '../shared/constants/app.constants.js';
import { OffersMetaService } from './offers-meta.service.js';
import { SessionService } from '../core/services/session/session.service.js';
import { CookieService } from '../core/services/cookie/cookie.service.js';

class _OffersService {
  constructor() {
    this.debug = new DebugService('OffersService');
    this._currentLoan = {};
    this._offers = [];
    this._agentGeneratedOffer = {};
    this._selectedOffer = {};
    this._isBureauPrefill = false;
    this._hasStips = false;
    this._hasSavings = false;
    this._isBigSaver = false;
    this._customerDetail = {};
    this._offerPageVisualExperienceResponse = {};
    this._session = SessionService.session || {};
    this._isReviewOfferCardTesting = false;
    window.mobileCheck = () => window.matchMedia('only screen and (max-width: 760px)').matches;
  }

  refreshSession() {
    SessionService.refreshSession();
    this._session = SessionService.session || {};
  }

  set isReviewOfferCardTesting(bool) {
    this._isReviewOfferCardTesting = bool;
  }

  get isBureauPrefill() {
    return this._isBureauPrefill;
  }

  set isBureauPrefill(bool) {
    this._isBureauPrefill = bool;
  }

  get customerDetail() {
    return this._customerDetail;
  }

  set customerDetail(customerDetail) {
    this._customerDetail = customerDetail;
  }

  get hasStips() {
    return this._hasStips;
  }

  set hasStips(bool) {
    this._hasStips = bool;
  }

  get hasSavings() {
    return this._hasSavings;
  }

  set hasSavings(bool) {
    this._hasSavings = bool;
  }

  get isBigSaver() {
    return this._isBigSaver;
  }

  get offerPageVisualExperienceResponse() {
    return this._offerPageVisualExperienceResponse;
  }

  set offerPageVisualExperienceResponse(offerPageVisualExperienceResponse) {
    this._offerPageVisualExperienceResponse = offerPageVisualExperienceResponse;
  }

  get offers() {
    let offers = OffersMetaService.sortOffers(this._offers, this.firstPossibleOffer(this._offers));
    offers = offers.filter(o => !o.crdtPlcyViolation && o.offerId !== 100);
    return offers.slice(0, 5);
  }

  get agentGeneratedOffer() {
    if (this._offers.length >= 1) {
      const agentGeneratedOffers = this._offers.filter(offer => offer.offerId === 100);
      if (agentGeneratedOffers.length) {
        return agentGeneratedOffers[0];
      }
    }
    return null;
  }

  set session(session) {
    this._session = session;
  }

  setOffers(offers, requestedOfferId) {
    this.debug.log('setting offers with', offers);
    const requestedOffer = offers.filter(offer => offer.offerId === requestedOfferId);
    const otherOffers = offers.filter(offer => offer.offerId !== requestedOfferId);
    otherOffers.sort((a, b) => a.offerId - b.offerId);
    this._offers = requestedOffer.concat(otherOffers);
    this.generateSavings();
    this._isBigSaver = this._offers[0].savings.lifetimeSavings > 1500 && this._offers[0].savings.monthlySavings > 25;
    OffersMetaService.addContextToOffers(this._offers, this._currentLoan);
  }

  getAllOffers() {
    const currentTerm = this.currentLoan.remainingPayment;
    const offers = this._offers.length > 1 ? this._offers.filter(offer => offer.term <= currentTerm + 12 && offer.term >= currentTerm - 12) : this._offers;
    return offers;
  }

  get currentLoan() {
    this.debug.log('getting current loan', this._currentLoan);
    return this._currentLoan;
  }

  set currentLoan(loan) {
    this.debug.log('setting currentLoan with', loan);
    this._currentLoan = loan;
  }

  setCurrentLoanFromSession(loan) {
    this.debug.log('setting currentLoan from session loan details', loan);
    this.currentLoan = {
      remainingPayment: loan.remainingTerm,
      currentMonthlyPayment: loan.monthlyPayment,
      apr: loan.apr,
    };
  }

  get selectedOffer() {
    return this._selectedOffer;
  }

  set selectedOffer(offer) {
    if (this._session.currentApp.statFlag === 11) {
      this._selectedOffer = offer;
      this.postSelectedOffer(offer.offerId).then(r => this.debug.log(r));
    } else {
      throw new Error(`appStatus is incorrect`);
    }
  }

  async setSelectedOffer(offer) {
    if (this._session.currentApp.statFlag === 11) {
      this._selectedOffer = offer;
      return this.postSelectedOffer(offer.offerId);
    }
    throw new Error(`appStatus is incorrect`);
  }

  firstPossibleOffer(offers) {
    if (offers.length === 1) {
      return offers[0];
    }
    const filteredOffer = offers.filter(o => o.offerId !== 100);
    const maxTerm = offers.find(offer => offer.term > 72) ? 84 : 72;
    return [
      ...filteredOffer.filter(o => this._currentLoan.remainingPayment <= 24 && o.term === 24),
      ...filteredOffer.filter(o => this._currentLoan.remainingPayment >= maxTerm && o.term >= maxTerm),
      ...filteredOffer.filter(o => o.term === this._currentLoan.remainingPayment),
      ...filteredOffer.filter(o => o.term > this._currentLoan.remainingPayment).sort((b, a) => b.term - a.term),
      ...filteredOffer.filter(o => o.term < this._currentLoan.remainingPayment).sort((b, a) => a.term - b.term),
    ][0];
  }

  async postSelectedOffer(offerId) {
    const { eAppId } = this._session;
    const headers = {
      headers: {
        C1_PRID: CookieService.getCookie('C1_PRID'),
      },
    };

    this.debug.log(headers.headers.C1_PRID ? 'Able to obtain C1_PRID value from Cookie' : 'Unable to obtain C1_PRID value from Cookie');

    const body = { offerId };
    let res;
    // Offers Bff Selected Offer Testing
    try {
      res = await ApiService.post(`${REFI_OFFERS_CONST.OFFERS_BFF_URL}${eAppId}/select-offer`, body, headers);
      this.debug.log(`Select Offer Success`, res);
    } catch (err) {
      this.debug.log(`Select Offer Post Failed.`);
      throw err;
    }
  }

  async getReviewOffer() {
    if (this._isReviewOfferCardTesting)
      return {
        offerApr: 9.95,
        offerPayment: 739.86,
        term: 10.0,
        savings: {
          lifetimeSavings: 6601.4,
          monthlySavings: 539.86,
        },
        currentLoan: {
          currentAPR: 3.0,
          currentTerm: 50.0,
        },
        offerValid: true,
      };

    const { eAppId } = this._session;
    const headers = {
      headers: {
        C1_PRID: CookieService.getCookie('C1_PRID'),
      },
    };

    this.debug.log(headers.headers.C1_PRID ? 'Able to obtain C1_PRID value from Cookie' : 'Unable to obtain C1_PRID value from Cookie');

    let res;
    try {
      res = await ApiService.get(`${REFI_OFFERS_CONST.OFFERS_BFF_URL}${eAppId}/review-offer`, headers, REFI_OFFERS_CONST.TIMEOUT_IN_MS);
      this.debug.log(`Review Offer Success`, res);
      return res;
    } catch (err) {
      this.debug.log(`Review Offer Get Failed.`, err);
      throw err;
    }
  }

  generateSavings() {
    this._offers = this._offers.map(_offer => {
      const offer = { ..._offer };
      if (!(offer.savings?.lifetimeSavings && offer.savings?.monthlySavings)) {
        offer.savings = {
          monthlySavings: this.calcMonthlySavings(this.currentLoan, offer),
          lifetimeSavings: this.calcLifetimeSavings(this.currentLoan, offer),
        };
      }
      return offer;
    });
  }

  calcMonthlySavings(currentLoan, offer) {
    return currentLoan.currentMonthlyPayment - offer.offerPayment;
  }

  calcLifetimeSavings(currentLoan, offer) {
    return currentLoan.currentMonthlyPayment * currentLoan.remainingPayment - offer.offerPayment * offer.term;
  }

  stratumReplacementValue(offer) {
    return {
      replacements: {
        value: `term: ${offer.term}, payment: ${offer.offerPayment}, apr: ${offer.offerApr}`,
      },
    };
  }
}

export const OffersService = new _OffersService();
